<template>
 <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="fw-bold text-dark mb-0">Ship Calls <span class="text-muted fs-5">({{ totalItems }})</span></h2>
    </div>

    <div class="glass-panel p-4 mb-4">
      <div class="row align-items-end g-3">
        <div class="col-md-4">
          <label class="form-label text-muted small fw-bold mb-2">Filter by Date</label>
          <div class="d-flex align-items-center">
            <b-form-datepicker class="flex-grow-1" :value="selectedDate" @input="val => { selectedDate = val; onFilterChange() }" placeholder="Select Date"></b-form-datepicker>
            <button v-if="selectedDate" @click="clearDateFilter" class="btn btn-outline-danger border-0 ms-2" title="Clear Date">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div class="col-md-4">
          <label class="form-label text-muted small fw-bold mb-2">Filter by Cruise Line</label>
          <div class="d-flex align-items-center">
            <b-form-select v-model="selectedCruiseLine" :options="cruiseLineOptions" class="flex-grow-1" @change="onFilterChange">
              <template #first>
                <option :value="null" disabled>Select Cruise Line</option>
              </template>
            </b-form-select>
            <button v-if="selectedCruiseLine" @click="clearCruiseLineFilter" class="btn btn-outline-danger border-0 ms-2" title="Clear Cruise Line">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div class="col-md-4">
          <label class="form-label text-muted small fw-bold mb-2">Filter by Tour Name</label>
          <div class="d-flex align-items-center">
            <b-form-select v-model="selectedTourName" :options="tourNameOptions" class="flex-grow-1" @change="onFilterChange">
              <template #first>
                <option :value="null" disabled>Select Tour Name</option>
              </template>
            </b-form-select>
            <button v-if="selectedTourName" @click="clearTourNameFilter" class="btn btn-outline-danger border-0 ms-2" title="Clear Tour Name">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <b-table-simple striped hover class="table-row-hover">
      <b-thead>
        <b-tr>
          <b-th>Date</b-th>
          <b-th>Tour Name</b-th>
          <b-th>Cruise Line</b-th>
          <b-th>Drop-off Location</b-th>
          <b-th>Pick-up Location</b-th>
          <b-th>Meeting Time</b-th>
          <b-th>Train Departure Time</b-th>
          <b-th>Actions</b-th>
        </b-tr>
      </b-thead>
      <b-tbody :style="loadingData ? 'opacity: 0.5; pointer-events: none;' : ''">
        <b-tr v-if="loadingData && templates.length === 0">
          <b-td colspan="8" class="text-center py-5">
            <span class="spinner-border text-primary"></span>
          </b-td>
        </b-tr>
        <b-tr v-else-if="templates.length === 0 && !loadingData">
           <b-td colspan="8" class="text-center py-5 text-muted">No ship calls found.</b-td>
        </b-tr>
        <template v-else>
          <b-tr v-for="template in templates" :key="template.id">
            <b-td @click="navigateToDetails(template.id)">{{ template.date }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ template.tour_name }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ template.cruise_line }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ template.drop_off_location }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ template.pick_up_location }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ formatTime(template.meeting_time) }}</b-td>
            <b-td @click="navigateToDetails(template.id)">{{ formatTime(template.train_departuretime) }}</b-td>
            <b-td :responsive="false">
              <router-link :to="{ name: 'qrcode', params: { id: template.id } }" class="btn btn-light btn-sm rounded-pill text-dark px-3 fw-bold me-2 text-decoration-none">
                <i class="fa fa-qrcode me-1"></i> QR
              </router-link>
              <router-link :to="{ name: 'pdf', params: { id: template.id } }" class="btn btn-light btn-sm rounded-pill text-success px-3 fw-bold me-2 text-decoration-none">
                <i class="fa fa-file-pdf me-1"></i> PDF
              </router-link>
              <router-link :to="{ name: 'editlist', params: { id: template.id } }" class="btn btn-light btn-sm rounded-pill text-primary px-3 fw-bold me-2 text-decoration-none">
                <i class="fa fa-pen me-1"></i> Edit
              </router-link>
              <button class="btn btn-light btn-sm rounded-pill text-danger px-3 fw-bold" @click="deleteTemplate(template.id)">
                <i class="fa fa-trash me-1"></i> Delete
              </button>
            </b-td>
          </b-tr>
        </template>
      </b-tbody>
    </b-table-simple>

    <!-- Pagination Controls -->
    <div class="d-flex justify-content-between align-items-center mt-4 mb-5" v-if="totalItems > 0">
      <div class="text-muted small">
        Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} entries
      </div>
      <b-pagination v-model="currentPage" :total-rows="totalItems" :per-page="pageSize" @change="onPageChange" class="mb-0" pills></b-pagination>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-confirm-modal" title="Confirm Deletion" hide-footer centered header-class="border-0 pb-0" body-class="p-4 text-center" content-class="border-0 shadow-lg rounded-4">
      <div class="mb-4">
        <i class="fa fa-exclamation-triangle text-danger mb-3" style="font-size: 3rem;"></i>
        <h4 class="fw-bold text-dark mb-2">Delete Ship Call?</h4>
        <p class="text-muted">This action cannot be undone. Are you sure you want to permanently delete this ship call schedule?</p>
      </div>
      <div class="d-flex justify-content-center gap-3 mt-4">
        <button type="button" class="btn btn-light rounded-pill px-4 fw-bold" @click="$bvModal.hide('delete-confirm-modal')">Cancel</button>
        <button type="button" class="btn btn-danger rounded-pill px-4 fw-bold shadow-sm" @click="confirmDelete" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Yes, Delete
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>

export default {
  data() {
    return {
      templates: [],
      selectedDate: null,
      selectedCruiseLine: null,
      selectedTourName: null,
      cruiseLineOptions: [],
      tourNameOptions: [],
      templateToDelete: null,
      loading: false,
      loadingData: false,
      currentPage: 1,
      pageSize: 20,
      totalItems: 0
    };
  },
  async mounted() {
    this.getFilterOptions();
    this.getItems();
  },
  methods: {
    onFilterChange() {
      this.currentPage = 1;
      this.getItems();
    },
    onPageChange() {
      this.getItems();
    },
    clearDateFilter() {
      this.selectedDate = null;
      this.onFilterChange();
    },
    clearCruiseLineFilter() {
      this.selectedCruiseLine = null;
      this.onFilterChange();
    },
    clearTourNameFilter() {
      this.selectedTourName = null;
      this.onFilterChange();
    },
    formatTime(time) {
      if (!time) return '';
      const [hours, minutes] = time.split(':').map(Number);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
    async getFilterOptions() {
      try {
        const [cruiseLinesRes, templatesRes] = await Promise.all([
          this.$xhr.get("/api/cruise-lines?pagination[pageSize]=1000&fields[0]=name"),
          this.$xhr.get("/api/sk-templates?pagination[pageSize]=1000&fields[0]=tour_name")
        ]);
        
        const cruiseLines = cruiseLinesRes.data.data.map(c => c.attributes.name).filter(Boolean);
        this.cruiseLineOptions = [...new Set(cruiseLines)].sort();

        const tourNames = templatesRes.data.data.map(t => t.attributes.tour_name).filter(Boolean);
        this.tourNameOptions = [...new Set(tourNames)].sort();
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    },
    async getItems() {
      this.loadingData = true;
      let url = `/api/sk-items?populate[sk_templates][populate]=cruise_line.logo&populate[drop_off_location][populate]=*&populate[pick_up_location][populate]=*`;
      
      url += `&pagination[page]=${this.currentPage}&pagination[pageSize]=${this.pageSize}&sort=date:desc`;
      
      if (this.selectedDate) {
        // If it's already in YYYY-MM-DD format (which b-form-datepicker emits by default), use it directly
        // This avoids timezone shifting issues caused by new Date() in different regions
        if (typeof this.selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(this.selectedDate)) {
          url += `&filters[date][$eq]=${this.selectedDate}`;
        } else {
          // Fallback parsing just in case it's a Date object or localized string
          const dateObj = new Date(this.selectedDate);
          if (!isNaN(dateObj.getTime())) {
            // Use getUTCFullYear to avoid shifting the day backwards in Western hemispheres
            const yyyy = dateObj.getUTCFullYear();
            const mm = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getUTCDate()).padStart(2, '0');
            const formattedDate = `${yyyy}-${mm}-${dd}`;
            url += `&filters[date][$eq]=${formattedDate}`;
          }
        }
      }
      if (this.selectedCruiseLine) {
        url += `&filters[sk_templates][cruise_line][name][$eq]=${encodeURIComponent(this.selectedCruiseLine)}`;
      }
      if (this.selectedTourName) {
        url += `&filters[sk_templates][tour_name][$eq]=${encodeURIComponent(this.selectedTourName)}`;
      }

      try {
        const result = await this.$xhr.get(url);
        
        // Flatten the deeply nested Strapi relations into a flat array
        // This prevents Vue 2 from spending hundreds of milliseconds recursively setting up reactivity
        this.templates = Object.freeze(result.data.data.map(item => ({
          id: item.id,
          date: item.attributes?.date,
          tour_name: item.attributes?.sk_templates?.data?.attributes?.tour_name,
          cruise_line: item.attributes?.sk_templates?.data?.attributes?.cruise_line?.data?.attributes?.name,
          drop_off_location: item.attributes?.drop_off_location?.data?.attributes?.name,
          pick_up_location: item.attributes?.pick_up_location?.data?.attributes?.name,
          meeting_time: item.attributes?.meeting_time,
          train_departuretime: item.attributes?.train_departuretime
        })));
        
        if (result.data.meta && result.data.meta.pagination) {
          this.totalItems = result.data.meta.pagination.total;
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        this.loadingData = false;
      }
    },
    deleteTemplate(id) {
      this.templateToDelete = id;
      this.$bvModal.show('delete-confirm-modal');
    },
    async confirmDelete() {
      if (!this.templateToDelete) return;
      this.loading = true;
      try {
        await this.$xhr.delete(`/api/sk-items/${this.templateToDelete}`);
        this.$bvToast.toast('Ship Call deleted!', { title: 'Deleted', variant: 'warning', solid: true });
        this.templates = this.templates.filter(template => template.id !== this.templateToDelete);
        this.$bvModal.hide('delete-confirm-modal');
      } catch (error) {
        console.error('Error deleting template:', error);
        this.$bvToast.toast('Delete failed', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
        this.templateToDelete = null;
      }
    },
    navigateToDetails(id) {
      this.$router.push({ name: 'clientpage', params: { id } });
    },
  },
};

</script>
<style>
.table-row-hover:hover {
  cursor: pointer;
}

.filter {
  max-width: 300px;
}


</style>
